import React, { useEffect, createContext, useState } from 'react'
import { createPortal } from 'react-dom'
import { observer } from 'mobx-react-lite'
import { useNavigate, useParams } from 'react-router'
// evertel
import { MessageWall } from '@evertel/web/message'
import { PhotoGallery, SlidePanel, SlidePanelHeader, SlidePanelBody, InfoBox, Row, Col, useUI } from '@evertel/web/ui'
import { useService } from '@evertel/di'
import { ThreadController } from '@evertel/thread'
import { APIDataThread } from '@evertel/types'
import { SessionService } from '@evertel/session/SessionService'
import { ThreadHeader } from './elements/ThreadHeader'
import { ThreadMediaSearch } from './elements/aside/ThreadMediaSearch'
import { ThreadDirectory } from './elements/aside/ThreadDirectory'
import { RightGutter } from './elements/aside/RightGutter'
import { MessageSearchModal } from '@evertel/web/message'
import { useDocumentTitleSuffix } from '@evertel/web/core'

interface ThreadContextProps {
    threadController: ThreadController
    setIsMessageSearchModalOpen: (state: boolean) => void
    toggleAside: (tab: string, intent: 'open' | 'close') => void
}

export const ThreadContext = createContext({} as ThreadContextProps)


const Thread = observer(() => {

    const sessionService = useService(SessionService, [])
    const threadController = useService(ThreadController, [])

    const { addToast } = useUI()
    const { setTitleSuffix } = useDocumentTitleSuffix()

    const navigate = useNavigate()

    const [isLoading, setIsLoading] = useState(false)
    const [showAsideTab, setShowAsideTab] = useState<string>('directory')
    const [openAside, setOpenAside] = useState(false)
    const [isMessageSearchModalOpen, setIsMessageSearchModalOpen] = useState(false)
    const [portalContainer, setPortalContainer] = useState<HTMLElement | null>(null)

    const asideTabs = {
        directory: {
            title: 'People in This Direct Message',
            component: ThreadDirectory
        },
        'media-search': {
            title: 'File Search',
            component: ThreadMediaSearch
        },
        closed: {
            title: null,
            component: () => null
        }
    }

    const handleCloseSlidePanel = () => {
        toggleAside(showAsideTab, 'close')
    }

    const { id: currentUrlIdString } = useParams<{ id: string }>()
    const currentThreadId = currentUrlIdString ? parseInt(currentUrlIdString, 10) : undefined

    useEffect(() => {

        if (!currentThreadId) {
            addToast({
                color: 'danger',
                message: 'No Direct Message specified.'
            })

            navigate('/landing', { replace: true })
            return
        }

        setIsLoading(true)

        // initialize controllers
        threadController.init(currentThreadId)
        threadController.fetchThreadDetails().catch((error) => {
            console.error('Thread init', error.message)
        })

        setIsLoading(false)

    }, [currentThreadId, sessionService.selectedDepartmentId])

    // Set document title suffix
    useEffect(() => {
        if (threadController.userInitials) {
            setTitleSuffix(threadController.userInitials)
        }
    }, [threadController.userInitials, setTitleSuffix])

    useEffect(() => {
        const container = document.getElementById('slide-panel-root')
        if (container) setPortalContainer(container)

        return () => { setPortalContainer(null) }
    }, [])

    const toggleAside = (tab = 'directory', intent: 'open' | 'close') => {
        if (intent === 'close') {
            setOpenAside(false)
            setShowAsideTab(null)
        } else if (intent === 'open') {
            setShowAsideTab(tab)
            setOpenAside(true)
        }
    }

    const contextValues = {
        threadController,
        setIsMessageSearchModalOpen,
        toggleAside
    }

    const thread = threadController.thread
    const AsideComponent = asideTabs[showAsideTab]?.component || asideTabs.closed.component

    if (!thread) {
        return (
            <InfoBox
                color="warning"
                className="m-3">
                Sorry, this thread was not found or you do not have access to it
            </InfoBox>
        )
    }

    return (
        <ThreadContext.Provider value={contextValues}>
            <PhotoGallery>
                <Row valign="stretch" className="thread-container h-100">
                    <Col className="w-100">
                        <ThreadHeader />
                        <MessageWall
                            modelId={threadController.threadId}
                            modelType="thread"
                            modelData={threadController.thread}
                            allowedToPost={!threadController.isBotThread}
                        />
                    </Col>

                    <RightGutter
                        activeTab={isMessageSearchModalOpen ? 'search' : (openAside && showAsideTab)}
                        toggleAside={toggleAside}
                    />
                    {portalContainer && createPortal(
                        <SlidePanel
                            id={`slidepanel-${showAsideTab}`} //must match gutter link id
                            className="room-aside"
                            visible={openAside}
                            onHide={() => handleCloseSlidePanel()}
                            backdrop={false}
                            placement="end"
                        >
                            <SlidePanelHeader title={asideTabs[showAsideTab]?.title} />
                            <SlidePanelBody>
                                <AsideComponent />
                            </SlidePanelBody>
                        </SlidePanel>,
                        portalContainer
                    )}
                </Row>

                <MessageSearchModal
                    visible={isMessageSearchModalOpen}
                    onClose={() => setIsMessageSearchModalOpen(false)}
                    modelType="thread"
                    modelId={threadController.threadId}
                    modelName={threadController.name}
                />

            </PhotoGallery>
        </ThreadContext.Provider>
    )
})

Thread.displayName = 'Thread'

export { Thread }
